import {
  request,
  signMessage,
  sendBtcTransaction,
  BitcoinNetworkType,
  AddressPurpose,
  RpcErrorCode,
  type WalletRequests,
  type Address,
  type BitcoinProvider as XverseBitcoinProvider,
  type SignMessageOptions,
  type SendBtcTransactionOptions,
  type ConnectParams,
  type RpcResult,
  type GetAccountParams,
} from 'sats-connect';

import { inWebView } from '@/utils/misc';

import { bitcoinNetworkTypeToConnectorNetwork } from '../../utils';

import ExtendedBaseConnector, { ParticleEvents } from './ExtendedBaseConnector';

enum XverseEvents {
  AccountChange = 'accountChange',
  Disconnect = 'disconnect',
}

function isXverseBitcoinProvider(provider: unknown): provider is XverseBitcoinProvider {
  return (
    !!provider &&
    typeof provider === 'object' &&
    typeof (provider as XverseBitcoinProvider).addListener === 'function' &&
    typeof (provider as XverseBitcoinProvider).request === 'function'
  );
}

export class XverseConnector extends ExtendedBaseConnector {
  readonly metadata: ExtendedBaseConnector['metadata'] = {
    id: 'xverse',
    name: 'Xverse Wallet',
    icon: '/wallets/xverse.png',
    downloadUrl: 'https://www.xverse.app',
  };

  constructor(private readonly isMobile: boolean) {
    super();

    if (isXverseBitcoinProvider(window.BitcoinProvider)) {
      // Pipe event from Xverse to Particle
      const accountChangeHandler = () => {
        void this.getAccount().then((addresses) => {
          this.eventEmitter.emit(
            ParticleEvents.AccountsChanged,
            addresses.map((item) => item.address)
          );
        });
      };

      const disconnectHandler = () => {
        void Promise.resolve(this.disconnect());
      };

      this.eventListeners.push(window.BitcoinProvider.addListener(XverseEvents.AccountChange, accountChangeHandler));
      this.eventListeners.push(window.BitcoinProvider.addListener(XverseEvents.Disconnect, disconnectHandler));
    }
  }

  isReady(): boolean {
    const isReady = typeof window !== 'undefined' && typeof window.BitcoinProvider !== 'undefined';

    if (this.isMobile && !inWebView()) {
      window.open(
        `https://connect.xverse.app/browser?url=${window.location.origin}/324seisfrunsessii1o`,
        '_blank',
        'noopener,noreferrer'
      );

      return true;
    }

    return isReady;
  }

  private loadAccounts = async (network: BitcoinNetworkType): Promise<Address[]> => {
    const action: keyof WalletRequests = 'wallet_connect';

    const params: ConnectParams = {
      addresses: [AddressPurpose.Payment, AddressPurpose.Ordinals],
      message: 'PACT SWAP dApp requests access to your BTC accounts',
    };

    const response: RpcResult<'wallet_connect'> = await request(action, params);

    if (response.status === 'success') {
      const paymentAddressItem = response.result.addresses.find(
        (address) => address.purpose === AddressPurpose.Payment
      );

      const ordinalsAddressItem = response.result.addresses.find(
        (address) => address.purpose === AddressPurpose.Ordinals
      );

      const addresses = [paymentAddressItem, ordinalsAddressItem].filter((addr): addr is Address => addr !== undefined);

      this.eventEmitter.emit(
        ParticleEvents.AccountsChanged,
        addresses.map((item) => item.address)
      );

      this.btcConnectStorage.setData(network, addresses);

      return addresses;
    } else if (response.status === 'error') {
      if (
        (response.error.code as RpcErrorCode) === RpcErrorCode.USER_REJECTION ||
        (response.error.code as RpcErrorCode) === RpcErrorCode.ACCESS_DENIED
      ) {
        // Handle user rejection or access denied errors
        const error = new Error(response.error.message) as Error & { code: number };

        error.code = 4001;
        throw error;
      } else {
        // Handle other errors
        throw new Error(response.error.message);
      }
    }

    return [];
  };

  async getAccount(): Promise<Address[]> {
    const action: keyof WalletRequests = 'wallet_getAccount';

    const params: GetAccountParams = null;

    const response: RpcResult<'wallet_getAccount'> = await request(action, params);

    if (response.status === 'success') {
      const paymentAddressItem = response.result.addresses.find(
        (address) => address.purpose === AddressPurpose.Payment
      );

      const ordinalsAddressItem = response.result.addresses.find(
        (address) => address.purpose === AddressPurpose.Ordinals
      );

      const addresses = [paymentAddressItem, ordinalsAddressItem].filter((addr): addr is Address => addr !== undefined);

      this.btcConnectStorage.setData(this.network, addresses);

      return addresses;
    } else if (response.status === 'error') {
      throw new Error(response.error.message);
    }

    return [];
  }

  sendInscription(): Promise<{ txid: string }> {
    throw new Error('Unsupported');
  }

  async requestAccounts(): Promise<string[]> {
    if (!this.isReady()) {
      throw new Error(`${this.metadata.name} is not install!`);
    }
    const addresses = await this.loadAccounts(this.network);

    return addresses.map((item) => item.address);
  }

  getAccounts(): Promise<string[]> {
    if (!this.isReady()) {
      throw new Error(`${this.metadata.name} is not install!`);
    }
    const accounts = this.btcConnectStorage.getData(this.network);

    const addresses = accounts.map((item) => item.address);

    (addresses as [] & { accounts: Address[] }).accounts = accounts;

    return Promise.resolve(addresses);
  }

  getPublicKey(): Promise<string> {
    if (!this.isReady()) {
      throw new Error(`${this.metadata.name} is not install!`);
    }

    const addresses = this.btcConnectStorage.getData(this.network);

    if (addresses.length) {
      return Promise.resolve(addresses[0].publicKey);
    } else {
      throw new Error('There are no public keys associated with current wallet');
    }
  }

  async signMessage(signStr: string): Promise<string> {
    if (!this.isReady()) {
      throw new Error(`${this.metadata.name} is not installed`);
    }

    const addresses = await this.getAccounts();

    if (addresses.length === 0) {
      throw new Error(`${this.metadata.name} not connected`);
    }
    const sig = await new Promise<string>((resolve, reject) => {
      const signMessageOptions: SignMessageOptions = {
        payload: {
          network: {
            type: this.network,
          },
          address: addresses[0],
          message: signStr,
        },
        onFinish: (response) => {
          resolve(response);
        },
        onCancel: () => {
          reject({
            code: 4001,
            message: 'User rejected the request.',
          });
        },
      };

      signMessage(signMessageOptions).catch((e) => {
        reject(e);
      });
    });

    const modifiedSig = Buffer.from(sig, 'base64');

    modifiedSig[0] = 31 + ((modifiedSig[0] - 31) % 4);

    return modifiedSig.toString('base64');
  }

  on(event: string, handler: (data?: unknown) => void) {
    return this.eventEmitter.on(event, handler);
  }

  removeListener(event: string, handler: (data?: unknown) => void) {
    return this.eventEmitter.removeListener(event, handler);
  }

  getProvider() {
    if (this.isReady()) {
      return window.BitcoinProvider;
    }
  }

  getNetwork(): Promise<'livenet' | 'testnet'> {
    if (!this.isReady()) {
      throw new Error(`${this.metadata.name} is not install!`);
    }

    return Promise.resolve(bitcoinNetworkTypeToConnectorNetwork(this.network));
  }

  switchNetwork(): Promise<void> {
    throw new Error('Unsupported');
  }

  async sendBitcoin(toAddress: string, satoshis: number): Promise<string> {
    const provider = this.getProvider();

    if (!provider) {
      throw new Error(`${this.metadata.name} is not install!`);
    }
    const addresses = await this.getAccounts();

    if (addresses.length === 0) {
      throw new Error(`${this.metadata.name} not connected!`);
    }
    const result = await new Promise<string>((resolve, reject) => {
      const sendBtcOptions: SendBtcTransactionOptions = {
        payload: {
          network: { type: this.network },
          recipients: [{ address: toAddress, amountSats: BigInt(satoshis) }],
          senderAddress: addresses[0],
        },
        onFinish: (response) => {
          resolve(response);
        },
        onCancel: () => {
          reject({ code: 4001, message: 'User rejected the request.' });
        },
      };

      sendBtcTransaction(sendBtcOptions).catch((e) => reject(e));
    });

    return result;
  }

  disconnect(): void {
    super.disconnect();
    this.btcConnectStorage.clearData();
  }
}
